import axios, { AxiosInstance, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { UiCommon } from '@/common/common';
import { Constants } from '@/constants/constants';
import { ApiConstant, apiserver } from '@/constants/api_constants';
import { Util } from '@/utils/utils';

interface ResponseHandler {
	(data: any): void;
}

interface ServerScrapParams {
	[key: string]: any; // 스크래핑에 들어가는 파라미터는 객체 형식으로 처리
}

interface ServerScrapResponse {
	code: string;
	content?: { responseData: string }; // `content`의 타입 명확화
	message?: string;
}

interface ExtendAxiosResponse extends AxiosResponse<Blob> {
	data: Blob & { filename?: string };
}

const Remote: any = {}; // Remote 객체 초기화
let queryString: string = Constants.empty; // 빈 문자열 상수

// Axios 클라이언트 인스턴스 설정
const client: AxiosInstance = axios.create({
	baseURL: apiserver()
});

const downloadClient: AxiosInstance = axios.create({
	baseURL: apiserver(),
	responseType: 'blob'
});

const rawDownloadClient: AxiosInstance = axios.create({
	baseURL: Constants.fileServer,
	responseType: 'blob'
});

/**
 * Remote API 설정 함수
 * @param headers 요청에 사용할 헤더 객체
 * @param clientType 클라이언트 타입: 'download', 'rawDownload', 또는 기본 클라이언트
 * @returns Remote.Api 객체 반환
 */
Remote.Api = function (
	headers: Record<string, string | number | boolean>,
	clientType: 'download' | 'rawDownload' | null = null
): typeof Remote.Api {
	if (typeof headers === 'object') {
		for (const key in headers) {
			if (Object.prototype.hasOwnProperty.call(headers, key)) {
				switch (clientType) {
					case 'download':
						(downloadClient.defaults.headers.common as AxiosRequestHeaders)[key] = headers[key];
						break;
					case 'rawDownload':
						(rawDownloadClient.defaults.headers.common as AxiosRequestHeaders)[key] = headers[key];
						break;
					default:
						(client.defaults.headers.common as AxiosRequestHeaders)[key] = headers[key];
						break;
				}
			}
		}
	}
	return Remote.Api;
};

Remote.Api.get = function (
	path: string,
	param?: Record<string, any> | string | number,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	queryString = Constants.empty;
	const url = Remote.Util.buildUrl(path, param);

	return new Promise(resolve => {
		client
			.get(url)
			.then((res: AxiosResponse) => {
				this.response = res;

				if (responseHandler === null) {
					resolve(res.data);
				} else {
					responseHandler(res.data);
				}
			})
			.catch(err => {
				const errorData = err.response?.data || null;

				if (responseHandler === null) {
					resolve(errorData);
				} else {
					responseHandler(errorData);
				}
			});
	});
};

Remote.Api.post = function (
	path: string,
	param: Record<string, any> | FormData | null,
	formData = false,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	// FormData로 변환할 경우
	if (formData && param !== undefined) {
		param = Util.convertFormData(param);
	}

	return new Promise(resolve => {
		client
			.post(path, param)
			.then((res: AxiosResponse) => {
				this.response = res;
				if (responseHandler === null) {
					resolve(res.data);
				} else {
					responseHandler(res.data);
				}
			})
			.catch(err => {
				const errorData = err.response?.data || null;

				if (responseHandler === null) {
					resolve(errorData);
				} else {
					responseHandler(errorData);
				}
			});
	});
};

Remote.Api.put = function (
	path: string,
	param: Record<string, any> | FormData | null,
	formData = false,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	// FormData 변환
	if (formData && param !== undefined) {
		param = Util.convertFormData(param);
	}

	return new Promise(resolve => {
		client
			.put(path, param)
			.then((res: AxiosResponse) => {
				if (responseHandler === null) {
					resolve(res.data);
				} else {
					responseHandler(res.data);
				}
			})
			.catch(err => {
				const errorData = err.response?.data || null;
				if (responseHandler === null) {
					resolve(errorData);
				} else {
					responseHandler(errorData);
				}
			});
	});
};

Remote.Api.patch = function (
	path: string,
	param: Record<string, any> | FormData | null,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	return new Promise(resolve => {
		client
			.patch(path, param)
			.then((res: AxiosResponse) => {
				this.response = res;

				if (responseHandler === null) {
					resolve(res.data);
				} else {
					responseHandler(res.data);
				}
			})
			.catch(err => {
				const errorData = err.response?.data || null;

				if (responseHandler === null) {
					resolve(errorData);
				} else {
					responseHandler(errorData);
				}
			});
	});
};

Remote.Api.delete = function (
	path: string,
	param?: Record<string, any> | string | number,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	const url = Remote.Util.buildUrl(path, param);

	queryString = Constants.empty;

	return new Promise(resolve => {
		client
			.delete(url)
			.then((res: AxiosResponse) => {
				this.response = res;

				if (responseHandler === null) {
					resolve(res.data);
				} else {
					responseHandler(res.data);
				}
			})
			.catch(err => {
				const errorData = err.response?.data || null;

				if (responseHandler === null) {
					resolve(errorData);
				} else {
					responseHandler(errorData);
				}
			});
	});
};

Remote.Api.download = function (
	path: string,
	param?: Record<string, any> | string | number,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	const url = Remote.Util.buildUrl(path, param);

	queryString = Constants.empty;

	return new Promise(resolve => {
		downloadClient
			.get(url)
			.then(async (res: ExtendAxiosResponse) => {
				this.response = res;

				// content-disposition 헤더가 없는 경우 (JSON 응답 처리)
				if (Util.isNullOrEmpty(res.headers['content-disposition'])) {
					const jsonResult = JSON.parse(await res.data.text());

					if (responseHandler === null) {
						resolve(jsonResult);
					} else {
						responseHandler(jsonResult);
					}
					return;
				}

				// 파일 이름 처리 및 파일 다운로드
				const contentDisposition = res.headers['content-disposition'];
				const nameMatch = contentDisposition.match(/filename="?([^"]+)"?/);

				if (nameMatch && nameMatch[1]) {
					const fileName = decodeURI(nameMatch[1]);
					res.data.filename = fileName;
					Remote.Util.downloadFile(res, fileName);
				}
			})
			.catch(error => {
				console.error(error);
				if (responseHandler === null) {
					resolve(error);
				} else {
					responseHandler(error);
				}
			});
	});
};

Remote.Api.downloadPost = function (
	path: string,
	param: Record<string, any> | FormData | null,
	responseHandler: ResponseHandler | null = null
): Promise<any> {
	return new Promise(resolve => {
		downloadClient
			.post(path, param)
			.then(async (res: ExtendAxiosResponse) => {
				this.response = res;

				// content-disposition 헤더 확인: 파일 다운로드 여부 검사
				if (Util.isNullOrEmpty(res.headers['content-disposition'])) {
					const jsonResult = JSON.parse(await res.data.text());

					if (responseHandler === null) {
						resolve(jsonResult);
					} else {
						responseHandler(jsonResult);
					}
					return;
				}

				// 파일명 처리
				const contentDisposition = res.headers['content-disposition'];
				const nameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
				let fileName = 'downloaded-file';

				if (nameMatch && nameMatch[1]) {
					fileName = decodeURI(nameMatch[1]);
				}

				// 다운로드 처리
				res.data.filename = fileName;
				Remote.Util.downloadFile(res, fileName);

				resolve({ code: Constants.apiSuccess, content: [] });
			})
			.catch(error => {
				console.error('Download Error:', error);

				if (responseHandler === null) {
					resolve(error);
				} else {
					responseHandler(error);
				}
			});
	});
};

Remote.Api.rawDownload = function (path: string, responseHandler: ResponseHandler | null = null): Promise<any> {
	return new Promise(resolve => {
		rawDownloadClient
			.get(path)
			.then((res: ExtendAxiosResponse) => {
				this.response = res;

				// 파일명 추출: path의 마지막 부분
				const pathSplit: string[] = path.split('/');
				const fileName: string = decodeURI(pathSplit[pathSplit.length - 1]);

				// 파일명 추가
				res.data.filename = fileName;

				// 파일 다운로드 처리
				Remote.Util.downloadFile(res, fileName);

				resolve({ code: Constants.apiSuccess, content: fileName });
			})
			.catch(error => {
				console.error('Download Error:', error);

				if (responseHandler === null) {
					resolve(error);
				} else {
					responseHandler(error);
				}
			});
	});
};

Remote.Api.serverScrap = async function (param: ServerScrapParams): Promise<any | null> {
	const jsonData = {
		params: {
			라이선스키: Constants.fsws.licenseKey,
			maxPool: Constants.fsws.maxPool,
			serviceUri: Constants.fsws.serviceUri,
			스크래핑: param
		}
	};

	try {
		// API 요청
		const response: ServerScrapResponse = await Remote.Api.post(ApiConstant.scrap.serverRequest, jsonData);

		// 에러 처리
		if (response.code !== Constants.apiSuccess || !response.content) {
			UiCommon.showLayerAlert(Constants.empty, response.message || 'Unknown error');
			return null;
		}

		// 성공 시 responseData 반환
		return JSON.parse(response.content.responseData);
	} catch (error) {
		console.error('Server Scrap Error:', error);
		UiCommon.showLayerAlert(Constants.empty, 'Server request failed.');
		return null;
	}
};
/**
 * Remote Util Functions
 */
Remote.Util = {};

Remote.Util.jsonToQSTemp = function (param: any, keyString = ''): string {
	let queryString = ''; // 함수 내부에서만 사용되는 쿼리 스트링

	const processParam = (param: any, keyString = ''): void => {
		if (typeof param === 'object' && param !== null) {
			if (Array.isArray(param)) {
				// 배열 처리
				for (let i = 0; i < param.length; i++) {
					if (typeof param[i] === 'object') {
						processParam(param[i], `${keyString}[${i}]`);
					} else if (keyString) {
						processParam(param[i], `${keyString}[${i}]`);
					}
				}
			} else {
				// 객체 처리
				for (const key in param) {
					if (Object.prototype.hasOwnProperty.call(param, key)) {
						const newKeyString = keyString ? `${keyString}.${key}` : key;
						processParam(param[key], newKeyString);
					}
				}
			}
		} else {
			// 기본 값 처리
			if (queryString) {
				queryString += '&';
			}
			let value: string | number = param;

			// 인코딩 체크 및 변환
			if (!this.encodingCheck(value)) {
				value = this.encodeURI(value);
			}

			queryString += `${keyString}=${value}`;
		}
	};

	processParam(param, keyString); // 재귀 호출로 처리
	return `?${queryString}`;
};

Remote.Util.encodeURI = function (contents: string) {
	return encodeURIComponent(contents);
};

Remote.Util.encodingCheck = function (contents: string) {
	const regCheck = /^[A-Za-z0-9]$/;
	return regCheck.test(contents);
};

Remote.Util.downloadFile = function (res: { data: Blob; headers?: Record<string, any> }, name: string): void {
	// Blob 객체를 URL로 변환
	const url: string = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type }));

	// a 태그를 생성하여 파일 다운로드 실행
	const link: HTMLAnchorElement = document.createElement('a');
	link.href = url;
	link.download = decodeURI(name); // 파일명 디코딩
	link.style.display = 'none';

	document.body.appendChild(link);
	link.click();

	// 사용한 링크 제거 및 메모리 해제
	link.remove();
	window.URL.revokeObjectURL(url);
};

/**
 * URL 생성 함수
 * @param path
 * @param param
 */
Remote.Util.buildUrl = function (path = Constants.empty, param: any): string {
	let url = path;
	if (typeof param === 'object') {
		url = path + Remote.Util.jsonToQSTemp(param);
	} else if (typeof param !== 'undefined') {
		url = `${path}/${param}`;
	}
	return url;
};

export { Remote };
